import { Link } from "react-router-dom";
import {
  Flame,
  Bot,
  Briefcase,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Calendar as CalendarIcon,
} from "lucide-react";

// AI Pipeline UI — Closing Layer Task 4 (additive view on /leads).
//
// Categorises every lead into one of 4 columns based on existing fields
// (intent_label, lifecycle, accelerator) — no new backend fields required.
//
// Columns:
//   1. High Intent   — intent_label === "High" AND lifecycle != MEETING_BOOKED
//   2. AI Engaged    — lifecycle ∈ {EMAILED, FOLLOWUP_PENDING} AND intent != High
//   3. Deals in Progress — lifecycle === REPLIED OR has accelerator suggestion
//   4. Meetings Booked — lifecycle === MEETING_BOOKED

const COLUMNS = [
  {
    key: "high_intent",
    label: "High Intent",
    icon: Flame,
    accent: "text-orange-700",
    bg: "bg-orange-50",
    border: "border-orange-200",
    dotBg: "bg-orange-500",
    description: "Most likely to convert",
  },
  {
    key: "ai_engaged",
    label: "AI Engaged",
    icon: Bot,
    accent: "text-indigo-700",
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    dotBg: "bg-indigo-500",
    description: "Outreach in progress",
  },
  {
    key: "deals_in_progress",
    label: "Deals in Progress",
    icon: Briefcase,
    accent: "text-violet-700",
    bg: "bg-violet-50",
    border: "border-violet-200",
    dotBg: "bg-violet-500",
    description: "Replied · negotiating",
  },
  {
    key: "meetings_booked",
    label: "Meetings Booked",
    icon: CheckCircle2,
    accent: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    dotBg: "bg-emerald-500",
    description: "Closing soon",
  },
];

function categoriseLead(lead) {
  const lc = (lead?.lifecycle || "").toUpperCase();
  const intent = (lead?.intent_label || "").trim();
  const status = (lead?.status || "").toLowerCase();
  if (status === "rejected" || status === "invalid") return null;
  if (lc === "MEETING_BOOKED") return "meetings_booked";
  if (lc === "REPLIED" || lead?.accelerator) return "deals_in_progress";
  if (intent === "High") return "high_intent";
  if (lc === "EMAILED" || lc === "FOLLOWUP_PENDING") return "ai_engaged";
  return null; // skip pending / new from the pipeline view
}

function formatNextAction(lead) {
  // Prefer the AI-accelerator's next_action if present, else the engine
  // mapping in lead.next_action, else a heuristic from lifecycle.
  const acc = lead?.accelerator;
  if (acc?.next_action) {
    const map = {
      call: "Call now",
      send_proof: "Send proof point",
      offer_pilot: "Offer pilot",
      book_meeting: "Book meeting",
      wait: "Wait — give it room",
      nurture: "Nurture",
    };
    return map[acc.next_action] || acc.next_action;
  }
  const next = (lead?.next_action || "").toUpperCase();
  if (next === "EMAIL") return "Send opener";
  if (next === "FOLLOWUP") return "Send follow-up";
  if (next === "BOOK_MEETING") return "Book a meeting";
  if (next === "CONFIRM") return "Send confirmation";
  if (next === "LINKEDIN") return "Reach via LinkedIn";
  if (next === "STOP") return "—";
  const lc = (lead?.lifecycle || "").toUpperCase();
  if (lc === "QUALIFIED") return "Send opener";
  if (lc === "EMAILED") return "Watch for reply";
  if (lc === "FOLLOWUP_PENDING") return "Follow-up sent";
  if (lc === "REPLIED") return "Book a meeting";
  if (lc === "MEETING_BOOKED") return "Confirm call";
  return "Review";
}

function aiSaysFor(lead) {
  // 1-line "AI says" summary — pulled from accelerator.reasoning if present,
  // else from intent_reason, else a friendly default.
  const acc = lead?.accelerator;
  if (acc?.reasoning) return acc.reasoning;
  if (lead?.intent_reason) return lead.intent_reason;
  if (lead?.reject_reason) return lead.reject_reason;
  return "Tracking signals — check back soon.";
}

function PipelineCard({ lead, columnKey }) {
  const intent = (lead?.intent_label || "").trim();
  const intentTone =
    intent === "High"
      ? "bg-orange-100 text-orange-800 border-orange-200"
      : intent === "Medium"
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : "bg-slate-100 text-slate-700 border-slate-200";
  const isMeetingBooked = columnKey === "meetings_booked";
  // Book likelihood chip — only shows when score is precomputed (no API call
  // per card). The Dashboard / Leads page can call /api/leads/score-all to
  // populate.
  const bookScore = Number.isFinite(lead?.book_score) ? Number(lead.book_score) : null;
  const bookColor = (lead?.book_score_color || "").trim();
  const bookTone =
    bookColor === "green"
      ? "bg-emerald-100 text-emerald-800 border-emerald-200"
      : bookColor === "yellow"
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : bookColor === "red"
      ? "bg-rose-100 text-rose-800 border-rose-200"
      : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <Link
      to={`/leads/${lead.id}`}
      data-testid={`pipeline-card-${lead.id}`}
      data-pipeline-column={columnKey}
      className="group block rounded-lg bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all duration-150 p-3.5 space-y-2.5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div
            className="font-semibold text-slate-900 text-[13.5px] truncate"
            title={lead?.name}
          >
            {lead?.name || "—"}
          </div>
          <div
            className="text-[11.5px] text-slate-500 truncate"
            title={`${lead?.title || ""} · ${lead?.company || ""}`}
          >
            {lead?.title || "—"}
            {lead?.company && (
              <span className="text-slate-400"> · {lead.company}</span>
            )}
          </div>
        </div>
        {intent && (
          <span
            className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${intentTone}`}
            data-testid={`pipeline-card-intent-${lead.id}`}
          >
            {intent}
          </span>
        )}
      </div>
      <div className="text-[11.5px] text-slate-600 leading-snug line-clamp-2">
        <span className="inline-flex items-center gap-1 text-indigo-600 font-semibold">
          <Sparkles className="h-3 w-3" /> AI says:
        </span>{" "}
        {aiSaysFor(lead)}
      </div>
      {bookScore !== null && !isMeetingBooked && (
        <div
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-bold border ${bookTone}`}
          data-testid={`pipeline-card-book-score-${lead.id}`}
          data-book-score={bookScore}
          title={lead?.book_score_reason || ""}
        >
          <Flame className="h-3 w-3" />
          {bookScore}% chance to book
        </div>
      )}
      <div className="flex items-center justify-between pt-1.5 border-t border-slate-100">
        <span
          className="text-[11px] font-medium text-slate-700 truncate"
          data-testid={`pipeline-card-next-${lead.id}`}
        >
          {isMeetingBooked ? (
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CalendarIcon className="h-3 w-3" />
              {lead?.meeting_start_time
                ? new Date(lead.meeting_start_time).toLocaleString(undefined, {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })
                : "Meeting scheduled"}
            </span>
          ) : (
            <>Next: {formatNextAction(lead)}</>
          )}
        </span>
        <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-indigo-600 transition-colors" />
      </div>
    </Link>
  );
}

export default function AiPipelineBoard({ leads }) {
  const buckets = COLUMNS.reduce((acc, c) => {
    acc[c.key] = [];
    return acc;
  }, {});
  for (const l of leads || []) {
    const k = categoriseLead(l);
    if (k && buckets[k]) buckets[k].push(l);
  }
  // Sort each column: Hot/High intent first, then most recent.
  for (const key of Object.keys(buckets)) {
    buckets[key].sort((a, b) => {
      const ai = a?.intent_label === "High" ? 0 : a?.intent_label === "Medium" ? 1 : 2;
      const bi = b?.intent_label === "High" ? 0 : b?.intent_label === "Medium" ? 1 : 2;
      if (ai !== bi) return ai - bi;
      return (b?.created_at || "").localeCompare(a?.created_at || "");
    });
  }

  return (
    <div
      data-testid="ai-pipeline-board"
      className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
    >
      {COLUMNS.map((col) => {
        const Icon = col.icon;
        const items = buckets[col.key];
        return (
          <div
            key={col.key}
            data-testid={`pipeline-column-${col.key}`}
            data-column-count={items.length}
            className={`rounded-xl border ${col.border} ${col.bg} flex flex-col`}
          >
            <div className="px-3.5 pt-3.5 pb-2.5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className={`h-7 w-7 rounded-md grid place-items-center bg-white border ${col.border}`}
                >
                  <Icon className={`h-3.5 w-3.5 ${col.accent}`} />
                </span>
                <div className="min-w-0">
                  <div
                    className={`text-[12.5px] font-bold uppercase tracking-wider ${col.accent} truncate`}
                  >
                    {col.label}
                  </div>
                  <div className="text-[10.5px] text-slate-500 leading-tight">
                    {col.description}
                  </div>
                </div>
              </div>
              <span
                className={`text-[11px] font-mono font-bold ${col.accent} bg-white border ${col.border} rounded-full px-2 py-0.5`}
                data-testid={`pipeline-column-count-${col.key}`}
              >
                {items.length}
              </span>
            </div>
            <div className="px-3 pb-3 space-y-2.5 flex-1 overflow-y-auto max-h-[680px]">
              {items.length === 0 ? (
                <div
                  className="text-[11.5px] text-slate-500 italic px-1 py-6 text-center"
                  data-testid={`pipeline-column-empty-${col.key}`}
                >
                  Empty for now.
                </div>
              ) : (
                items.map((l) => (
                  <PipelineCard key={l.id} lead={l} columnKey={col.key} />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
